import { Client, type Room } from 'colyseus.js';
import { devSignIn, saveAuth, loadAuth, type AuthState } from './auth.js';
import { NarratorDirector } from './narrator/director.js';

const $ = (id: string): HTMLElement | null => document.getElementById(id);

const BACKEND_WS = import.meta.env.VITE_BACKEND_URL ?? 'ws://localhost:2567';
const BACKEND_HTTP = BACKEND_WS.replace('ws://', 'http://').replace('wss://', 'https://');

let auth: AuthState | null = null;
let client: Client | null = null;
let gameRoom: Room | null = null;

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

// Colyseus reconnection token (Camada 1). Tab-scoped (sessionStorage) — it's for resuming the same
// session across a reload/blip, not for sharing a run between tabs. Must stay under the server's
// allowReconnection window (RECONNECT_WINDOW_SECONDS = 60s in GameRoom).
const RECONNECT_KEY = 'game.reconnect.v1';
const RECONNECT_WINDOW_MS = 55_000;
const RECONNECT_RETRY_MS = 1_500;
const saveReconnectToken = (t: string): void => {
  try { sessionStorage.setItem(RECONNECT_KEY, t); } catch { /* storage off */ }
};
const loadReconnectToken = (): string | null => {
  try { return sessionStorage.getItem(RECONNECT_KEY); } catch { return null; }
};
const clearReconnectToken = (): void => {
  try { sessionStorage.removeItem(RECONNECT_KEY); } catch { /* storage off */ }
};

// Pixel-art combat render runs in an isolated iframe (/combat.html?live=1). We forward each
// GameState snapshot to it via postMessage; it maps state -> visuals (applyState). The iframe
// announces 'combat-ready' on load so we can (re)send the latest snapshot past the load race.
let combatFrame: HTMLIFrameElement | null = null;
let lastCombatSnapshot: unknown = null;
const postCombat = (snap: unknown): void => {
  lastCombatSnapshot = snap;
  combatFrame ??= document.getElementById('combat-frame') as HTMLIFrameElement | null;
  combatFrame?.contentWindow?.postMessage({ type: 'gamestate', state: snap }, '*');
};
const postCombatMsg = (msg: Record<string, unknown>): void => {
  combatFrame ??= document.getElementById('combat-frame') as HTMLIFrameElement | null;
  combatFrame?.contentWindow?.postMessage(msg, '*');
};

// Full-screen fade (PLAY -> combat). #fade sits above every screen; 'on' = opaque.
const fade = (black: boolean, ms = 350): Promise<void> => new Promise((resolve) => {
  const el = $('fade');
  if (!el) { resolve(); return; }
  el.style.transitionDuration = String(ms) + 'ms';
  el.classList.toggle('on', black);
  setTimeout(resolve, ms);
});
let screenReady: Promise<void> = Promise.resolve();   // resolves once the combat screen is faded in

// The Keeper (narrator): decides when/what to say; the pixel iframe types + voices it.
// Floor entry: the server parks in 'mob_intro' until START_FIGHT; we use that pause for his line
// and send START_FIGHT when the iframe reports the line finished (or straight away if he is quiet).
// Lines fired before the iframe has loaded (a reload restoring straight into a run) are queued
// and delivered once its script is up. Readiness is checked directly on the (same-origin) frame —
// its 'combat-ready' message can fire before this module even runs, so a flag would race.
const pendingSay: Record<string, unknown>[] = [];   // a queue: an 'intro' must never be dropped (it gates START_FIGHT)
let sayFlush: ReturnType<typeof setInterval> | null = null;
const combatIsReady = (): boolean => {
  combatFrame ??= document.getElementById('combat-frame') as HTMLIFrameElement | null;
  const w = combatFrame?.contentWindow as (Window & { combatApplyState?: unknown }) | null | undefined;
  return typeof w?.combatApplyState === 'function';
};
const flushSay = (): void => {
  if (!combatIsReady()) return;
  for (const m of pendingSay.splice(0)) postCombatMsg(m);
  if (sayFlush) { clearInterval(sayFlush); sayFlush = null; }
};
const sayToCombat = (msg: Record<string, unknown>): void => {
  if (combatIsReady()) { postCombatMsg(msg); return; }
  pendingSay.push(msg);
  sayFlush ??= setInterval(flushSay, 150);
};
const narrator = new NarratorDirector((text, opts) => {
  sayToCombat({ type: 'say', text, hold: opts.hold, where: opts.where ?? 'box' });
});
let introKey = '';   // runId@floor of the intro already dispatched
// personal best floor, kept by the narrator director in localStorage ('caterva.narrator')
const bestFloorLocal = (): number => {
  try { const p = JSON.parse(localStorage.getItem('caterva.narrator') ?? '{}') as { best?: number }; return Number(p.best) || 0; } catch { return 0; }
};
window.addEventListener('message', (e: MessageEvent) => {
  const d = e.data as { type?: string; itemId?: string; action?: string; qteId?: string } | null;
  // any interaction inside a pixel page counts as the gesture that unlocks audio here
  if (d?.type === 'gesture' || d?.type === 'ui-action') { ambienceTryStart(); gmTryStart(); }
  if (d?.type === 'combat-ready') {
    if (lastCombatSnapshot) combatFrame?.contentWindow?.postMessage({ type: 'gamestate', state: lastCombatSnapshot }, '*');
    flushSay();
  } else if (d?.type === 'pick-equip' && d.itemId) {
    sendGame('PICK_EQUIP', { itemId: d.itemId });
  } else if (d?.type === 'reroll') {
    sendGame('REROLL_EQUIP');
  } else if (d?.type === 'enter-floor') {
    sendGame('ENTER_FLOOR');
  } else if (d?.type === 'qte-click' && d.qteId) {
    sendGame('QTE_CLICK', { qteId: d.qteId });
  } else if (d?.type === 'intro-done') {
    sendGame('START_FIGHT');
  } else if (d?.type === 'volume') {
    const v = (d as { value?: number }).value;
    if (typeof v === 'number') setMusicMaster(v);
  } else if (d?.type === 'give-up') {
    sendGame('GIVE_UP');   // settings overlay: abandon the run (server ends it like a death)
  } else if (d?.type === 'pause') {
    sendGame('PAUSE');
  } else if (d?.type === 'resume') {
    sendGame('RESUME');
  } else if (d?.type === 'return-menu') {
    // game-over screen -> back to the lobby. Consented leave (code 1000), so nothing tries to
    // reconnect to the finished run; the next PLAY joins a fresh room.
    clearReconnectToken();
    void gameRoom?.leave(true);
    gameRoom = null;
    showScreen('lobby');
  } else if (d?.type === 'ui-action') {
    // pixel sign-in / main screens (iframes) forward their button intents here
    if (d.action === 'sign-in') void handleDevSignIn();
    else if (d.action === 'play') void startCombat();
    // 'leaderboard' — TODO when the leaderboard screen exists
  }
});

// Menu ambience (sign-in + lobby), owned by THIS window so it runs continuously across the two
// pixel screens (each is its own iframe) and stops for combat. Browsers gate audio behind a user
// gesture; clicks inside our same-origin iframes activate this window too, and the pixel pages
// also post a 'gesture' message so a pending start can retry (e.g. after a reload into the lobby).
// Two loops layered at the same level (both normalized to the mine's loudness): the old mine +
// the blizzard wind (user add 2026-08-26).
// per-track volumes: mine 0.35 -> 0.10 -> 0.20 (2026-08-25); blizzard 0.20 -> 0.25 (2026-08-26)
const AMBIENCE_TRACKS: { src: string; volume: number }[] = [
  { src: '/ambience/old-mine.mp3', volume: 0.20 },
  { src: '/ambience/blizzard.mp3', volume: 0.25 },
];
const AMBIENCE_FADE_MS = 700;
const ambience: HTMLAudioElement[] = [];
let ambienceWanted = false;
let ambienceFade: ReturnType<typeof setInterval> | null = null;
// ramp every track from its current level to (its own volume × `to`), to = 0..1
const ambienceRamp = (to: number, onDone?: () => void): void => {
  if (!ambience.length) return;
  if (ambienceFade) clearInterval(ambienceFade);
  const from = ambience.map((el) => el.volume);
  const steps = 14;
  let i = 0;
  ambienceFade = setInterval(() => {
    i += 1;
    ambience.forEach((el, k) => {
      const target = (AMBIENCE_TRACKS[k]?.volume ?? 0) * to * musicMaster;
      el.volume = Math.max(0, Math.min(1, (from[k] ?? 0) + ((target - (from[k] ?? 0)) * i) / steps));
    });
    if (i >= steps) {
      if (ambienceFade) clearInterval(ambienceFade);
      ambienceFade = null;
      onDone?.();
    }
  }, AMBIENCE_FADE_MS / steps);
};
const ambienceTryStart = (): void => {
  if (!ambienceWanted) return;
  if (!ambience.length) {
    AMBIENCE_TRACKS.forEach((t, i) => {
      const el = Object.assign(new Audio(t.src), { loop: true, preload: 'auto', hidden: true });
      el.id = 'ambience' + (i ? String(i + 1) : '');
      document.body.appendChild(el);
      ambience.push(el);
    });
  }
  if (ambience.every((el) => !el.paused)) return;
  for (const el of ambience) el.volume = 0;
  Promise.all(ambience.map((el) => (el.paused ? el.play() : Promise.resolve())))
    .then(() => { ambienceRamp(1); })
    .catch(() => { /* no gesture yet — retried on the next one */ });
};
const setAmbience = (on: boolean): void => {
  ambienceWanted = on;
  if (on) { ambienceTryStart(); return; }
  if (ambience.some((el) => !el.paused)) ambienceRamp(0, () => { if (!ambienceWanted) for (const el of ambience) el.pause(); });
};

// In-game music: playlist crossfaded between two <audio> elements so the loop is seamless (a single
// track crossfades into itself). Shuffled with no immediate repeat when there is more than one.
// Runs while the combat screen is up; fades out on leave. User's picks 2026-08-26: three fronbondi_skegs
// ambients, baked offline: loudnorm -18.5 LUFS, low-shelf +9 dB @110 Hz, 8-bit/hold-3 crusher,
// 5 s cathedral convolution (60% wet, 60 ms pre-delay). Shuffled, no immediate repeat.
const GAME_MUSIC = [   // two-worlds dropped, universfield pair added 2026-08-26
  '/ambience/game/drone-unsettling.mp3', '/ambience/game/ulterior-motives.mp3',
  '/ambience/game/mystery-horror.mp3', '/ambience/game/horror-atmosphere-03.mp3',
];
const GAME_MUSIC_VOLUME = 0.19;   // 0.18 -> 0.10 -> 0.20 -> 0.25 -> 0.19 (-25%, user tuning 2026-08-26)
// Player's music volume (0..1) from the pause menu; the combat iframe owns the control and persists it
// under the same key, and posts {type:'volume'} on change. Scales the game music AND the menu ambience.
const VOLUME_KEY = 'caterva.volume';
const loadMusicMaster = (): number => {
  try { const v = (JSON.parse(localStorage.getItem(VOLUME_KEY) ?? '{}') as { music?: number }).music; return typeof v === 'number' ? Math.max(0, Math.min(1, v)) : 1; } catch { return 1; }
};
let musicMaster = loadMusicMaster();
const setMusicMaster = (v: number): void => {
  musicMaster = Math.max(0, Math.min(1, v));
  const el = gm.els[gm.active];
  if (el && !gm.fades[gm.active]) el.volume = GAME_MUSIC_VOLUME * musicMaster;
  if (ambienceWanted && !ambienceFade) ambience.forEach((a, k) => { a.volume = (AMBIENCE_TRACKS[k]?.volume ?? 0) * musicMaster; });
};
const GAME_FADE_IN_S = 3;         // first track of a session fades in; later tracks crossfade
const GAME_XFADE_S = 6;   // 2 -> 6: the 2 s crossfade read as a cut (user 2026-08-26)
const gm = { els: [] as HTMLAudioElement[], active: -1, track: -1, wanted: false, switching: false, fades: [] as (ReturnType<typeof setInterval> | null)[] };
const gmRamp = (i: number, to: number, ms: number, onDone?: () => void): void => {
  const el = gm.els[i];
  if (!el) return;
  const prev = gm.fades[i];
  if (prev) clearInterval(prev);
  const from = el.volume;
  const steps = Math.max(4, Math.round(ms / 50));
  let n = 0;
  gm.fades[i] = setInterval(() => {
    n += 1;
    el.volume = Math.max(0, Math.min(1, from + ((to - from) * n) / steps));
    if (n >= steps) {
      const h = gm.fades[i];
      if (h) clearInterval(h);
      gm.fades[i] = null;
      onDone?.();
    }
  }, ms / steps);
};
// playlist order, looping (was shuffled; the same track could follow itself across a crossfade)
const gmPickNext = (): number => (gm.track + 1) % GAME_MUSIC.length;
const gmInit = (): void => {
  if (gm.els.length) return;
  for (let i = 0; i < 2; i += 1) {
    const el = Object.assign(new Audio(), { preload: 'auto', hidden: true });
    el.id = 'game-music-' + String(i);
    el.addEventListener('timeupdate', () => { if (i === gm.active) gmMaybeCrossfade(); });
    el.addEventListener('ended', () => { if (i === gm.active) gmStartNext(); });
    document.body.appendChild(el);
    gm.els.push(el);
    gm.fades.push(null);
  }
};
// start the next track on the idle element, fading it in while the current one fades out
const gmStartNext = (): void => {
  if (!gm.wanted || gm.switching) return;
  gm.switching = true;
  const next = gm.active === -1 ? 0 : 1 - gm.active;
  const prev = gm.active;
  const el = gm.els[next];
  if (!el) { gm.switching = false; return; }
  gm.track = gmPickNext();
  el.src = GAME_MUSIC[gm.track] ?? '';
  el.volume = 0;
  el.currentTime = 0;
  el.play().then(() => {
    gm.active = next;
    gmRamp(next, GAME_MUSIC_VOLUME * musicMaster, (prev === -1 ? GAME_FADE_IN_S : GAME_XFADE_S) * 1000);
    if (prev !== -1) gmRamp(prev, 0, GAME_XFADE_S * 1000, () => { gm.els[prev]?.pause(); });
    gm.switching = false;
  }).catch(() => { gm.switching = false; /* no gesture yet — retried on the next one */ });
};
const gmMaybeCrossfade = (): void => {
  const el = gm.els[gm.active];
  if (!el || gm.switching || !gm.wanted) return;
  if (Number.isFinite(el.duration) && el.duration - el.currentTime <= GAME_XFADE_S) gmStartNext();
};
const gmTryStart = (): void => {
  if (!gm.wanted) return;
  gmInit();
  const cur = gm.els[gm.active];
  if (cur && !cur.paused) return;
  gmStartNext();
};
const setGameMusic = (on: boolean): void => {
  gm.wanted = on;
  if (on) { gmTryStart(); return; }
  gm.els.forEach((el, i) => { if (!el.paused) gmRamp(i, 0, 700, () => { if (!gm.wanted) el.pause(); }); });
  gm.active = -1;
};

const showScreen = (screen: 'sign-in' | 'lobby' | 'combat'): void => {
  for (const id of ['sign-in-screen', 'lobby-screen', 'combat-screen']) {
    const el = $(id);
    if (el) el.style.display = 'none';
  }
  const active = $(screen + '-screen');
  if (active) active.style.display = screen === 'sign-in' ? 'flex' : 'block';
  setAmbience(true);                  // the mine ambience runs on EVERY screen (user call 2026-08-25)
  setGameMusic(screen === 'combat');  // the music layers on top of it in combat
};

const showError = (msg: string): void => {
  const el = $('sign-in-error');
  if (el) el.textContent = msg;
};

const setText = (id: string, text: string): void => {
  const el = $(id);
  if (el) el.textContent = text;
};

let lastLoggedKey = '';
let lastQteAnimated = '';

const addLog = (text: string, cls = ''): void => {
  const log = $('combat-log');
  if (!log) return;
  const entry = document.createElement('div');
  entry.className = 'log-entry' + (cls ? ' ' + cls : '');
  entry.textContent = text;
  log.appendChild(entry);
  log.scrollTop = log.scrollHeight;
};

const clearLog = (): void => {
  const log = $('combat-log');
  if (log) log.replaceChildren();
};

const buildBar = (current: number, max: number, width: number): string => {
  const filled = Math.round((current / max) * width);
  const empty = width - filled;
  return '█'.repeat(filled) + '░'.repeat(empty);
};

const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X'];

const toRoman = (n: number): string => ROMAN[n] ?? String(n);

interface EquippedInfo {
  name: string;
  rarity: string;
  description: string;
}

const SLOT_LABELS: Record<string, string> = {
  sword: 'SWORD',
  helmet: 'HELMET',
  chestplate: 'CHEST',
  leggings: 'LEGS',
  boots: 'BOOTS',
};

const ALL_SLOTS = ['sword', 'helmet', 'chestplate', 'leggings', 'boots'];

const renderEquipPanel = (equippedJson: string, slotLevelsJson: string): void => {
  const panel = $('equip-panel');
  if (!panel) return;

  let equipped: Record<string, EquippedInfo> = {};
  let levels: Record<string, number> = {};
  try { equipped = JSON.parse(equippedJson) as Record<string, EquippedInfo>; } catch { /* empty */ }
  try { levels = JSON.parse(slotLevelsJson) as Record<string, number>; } catch { /* empty */ }

  panel.replaceChildren();

  for (const slot of ALL_SLOTS) {
    const row = document.createElement('div');
    row.className = 'equip-slot';

    const label = document.createElement('span');
    label.className = 'slot-label';
    label.textContent = SLOT_LABELS[slot] ?? slot;

    const item = equipped[slot];
    const lvl = levels[slot] ?? 0;

    if (item) {
      const right = document.createElement('span');
      right.style.display = 'flex';
      right.style.alignItems = 'center';

      const nameEl = document.createElement('span');
      const rarityClass = item.rarity === 'epic' ? 'epic' : item.rarity === 'rare' ? 'rare' : '';
      nameEl.className = 'slot-item' + (rarityClass ? ' ' + rarityClass : '');
      const levelSuffix = lvl > 1 ? ' ' + toRoman(lvl) : '';
      nameEl.textContent = item.name + levelSuffix;

      const info = document.createElement('span');
      info.className = 'slot-info';
      info.textContent = 'i';
      const tooltip = document.createElement('span');
      tooltip.className = 'slot-tooltip';
      tooltip.textContent = item.description;
      info.appendChild(tooltip);

      right.append(nameEl, info);
      row.append(label, right);
    } else {
      const empty = document.createElement('span');
      empty.className = 'slot-empty';
      empty.textContent = '—';
      row.append(label, empty);
    }

    panel.appendChild(row);
  }
};

const renderRewardEquipped = (equippedJson: string, slotLevelsJson: string): void => {
  const container = $('reward-equipped');
  if (!container) return;

  let equipped: Record<string, EquippedInfo> = {};
  let levels: Record<string, number> = {};
  try { equipped = JSON.parse(equippedJson) as Record<string, EquippedInfo>; } catch { /* empty */ }
  try { levels = JSON.parse(slotLevelsJson) as Record<string, number>; } catch { /* empty */ }

  container.replaceChildren();

  const filledSlots = ALL_SLOTS.filter((s) => equipped[s]);
  if (filledSlots.length === 0) return;

  const header = document.createElement('div');
  header.style.cssText = 'color: #555; font-size: 9px; letter-spacing: 0.15em; margin-bottom: 4px; text-align: center;';
  header.textContent = 'EQUIPPED';
  container.appendChild(header);

  for (const slot of filledSlots) {
    const item = equipped[slot];
    if (!item) continue;
    const lvl = levels[slot] ?? 0;
    const row = document.createElement('div');
    row.className = 'equip-slot';
    const lbl = document.createElement('span');
    lbl.className = 'slot-label';
    lbl.textContent = SLOT_LABELS[slot] ?? slot;
    const nameEl = document.createElement('span');
    const rarityClass = item.rarity === 'epic' ? 'epic' : item.rarity === 'rare' ? 'rare' : '';
    nameEl.className = 'slot-item' + (rarityClass ? ' ' + rarityClass : '');
    const levelSuffix = lvl > 1 ? ' ' + toRoman(lvl) : '';
    nameEl.textContent = item.name + levelSuffix + ' (' + item.description + ')';
    row.append(lbl, nameEl);
    container.appendChild(row);
  }
};

interface EquipChoice {
  itemId: string;
  name: string;
  slot: string;
  rarity: string;
  description: string;
}

const SLOT_ICONS: Record<string, string> = {
  sword: '⚔',
  helmet: '⛑',
  chestplate: '🛡',
  leggings: '⬡',
  boots: '⬢',
};

interface CurrentStats {
  atkDmg: number;
  atkSpdMs: number;
  resistance: number;
  maxHp: number;
  healOnKillPct: number;
}

const formatStatDelta = (item: EquipChoice, stats: CurrentStats): string => {
  const parts: string[] = [];
  const d = item.description;

  const parseVal = (pattern: RegExp): number => {
    const m = pattern.exec(d);
    return m ? parseInt(m[1] ?? '0', 10) : 0;
  };

  if (d.includes('ATK')) {
    const add = parseVal(/\+(\d+)\s*ATK/);
    if (add > 0) {
      const pct = stats.atkDmg > 0 ? Math.round((add / stats.atkDmg) * 100) : 0;
      parts.push(String(stats.atkDmg) + ' → ' + String(stats.atkDmg + add) + ' ATK (+' + String(pct) + '%)');
    }
  }
  if (/\+(\d+)\s*HP\b/.test(d)) {
    const add = parseVal(/\+(\d+)\s*HP\b/);
    if (add > 0) {
      const pct = stats.maxHp > 0 ? Math.round((add / stats.maxHp) * 100) : 0;
      parts.push(String(stats.maxHp) + ' → ' + String(stats.maxHp + add) + ' HP (+' + String(pct) + '%)');
    }
  }
  // v4 build stats (DEF points, % SPD/CRIT/LEECH/BLOCK/QTE, focus): shown as written on the item
  for (const piece of d.split(' · ')) {
    if (/\s(ATK|HP)$/.test(piece)) continue;
    parts.push(piece);
  }

  return parts.length > 0 ? parts.join(' · ') : item.description;
};

const renderEquipment = (itemsJson: string, rerollCostVal: number, coins: number, stats?: CurrentStats): void => {
  const container = $('card-choices');
  if (!container) return;

  let items: EquipChoice[] = [];
  try {
    items = JSON.parse(itemsJson) as EquipChoice[];
  } catch {
    items = [];
  }

  container.replaceChildren();

  if (items.length === 0) return;

  const header = document.createElement('div');
  header.className = 'card-choices-header';
  header.textContent = 'CHOOSE EQUIPMENT';
  container.appendChild(header);

  const grid = document.createElement('div');
  grid.className = 'card-grid';

  for (const item of items) {
    const btn = document.createElement('button');
    btn.className = 'card-btn';
    const rarityClass = item.rarity === 'epic' ? 'epic' : item.rarity === 'rare' ? 'rare' : '';

    const slotTag = document.createElement('div');
    slotTag.className = 'card-rarity' + (rarityClass ? ' ' + rarityClass : '');
    slotTag.textContent = (SLOT_ICONS[item.slot] ?? '') + ' ' + item.slot.toUpperCase() + ' · ' + item.rarity.toUpperCase();

    const name = document.createElement('div');
    name.className = 'card-name';
    name.textContent = item.name;

    const desc = document.createElement('div');
    desc.className = 'card-desc';
    desc.textContent = stats ? formatStatDelta(item, stats) : item.description;

    btn.append(slotTag, name, desc);
    btn.addEventListener('click', () => {
      sendGame('PICK_EQUIP', { itemId: item.itemId });
    });
    grid.appendChild(btn);
  }

  container.appendChild(grid);

  const rerollDiv = document.createElement('div');
  rerollDiv.className = 'reroll-line';

  const rerollBtn = document.createElement('button');
  rerollBtn.className = 'btn btn-secondary';
  const canAfford = coins >= rerollCostVal;
  rerollBtn.textContent = 'REROLL — ' + String(rerollCostVal) + ' COINS';
  rerollBtn.disabled = !canAfford;
  rerollBtn.addEventListener('click', () => {
    sendGame('REROLL_EQUIP');
  });

  rerollDiv.appendChild(rerollBtn);
  container.appendChild(rerollDiv);
};

const enterLobby = async (): Promise<void> => {
  if (!auth) return;
  client = new Client(BACKEND_WS);

  setText('lobby-wallet', auth.wallet.slice(0, 4) + '...' + auth.wallet.slice(-4));

  try {
    const res = await fetch(BACKEND_HTTP + '/health');
    const data = (await res.json()) as { network?: string };
    setText('lobby-network', data.network ?? 'unknown');
  } catch {
    // non-critical
  }

  showScreen('lobby');

  try {
    const room = await client.joinOrCreate('lobby');
    room.onStateChange((state: { connectedCount?: number }) => {
      setText('lobby-online', String(state.connectedCount ?? 0));
    });
  } catch {
    setText('lobby-online', '?');
  }
};

// All room messages go through here. While the room is down (reconnecting after a silent socket
// drop — common on an idle shop screen over a tunnel/wifi), a player's shop intent would be
// silently lost and the click "does nothing". Instead: keep the LATEST intent per type and flush
// it once the room is rebound. Time-sensitive messages (QTE click, pause/resume) are not queued.
// Shop/flow intents stay PENDING until the server state confirms them — a message sent into a
// half-dead socket (link died without a close frame; the watchdog needs a few seconds to notice)
// is otherwise lost while the client still believes it was delivered. Confirmation = the state the
// intent was meant to change actually changed: PICK/ENTER/START -> status moved on; REROLL -> the
// card set changed. Anything still pending when the room is rebound is re-sent. No double effects:
// a re-sent PICK after the floor advanced is ignored server-side; a REROLL is only re-sent while
// the cards are still the ones the player rerolled from.
const QUEUEABLE = new Set(['PICK_EQUIP', 'REROLL_EQUIP', 'ENTER_FLOOR', 'START_FIGHT']);
interface PendingIntent { payload: unknown; status: string; choices: string }
const pendingIntents = new Map<string, PendingIntent>();
let lastStatus = '';
let lastChoices = '';
const sendGame = (type: string, payload?: unknown): void => {
  if (QUEUEABLE.has(type)) pendingIntents.set(type, { payload, status: lastStatus, choices: lastChoices });
  gameRoom?.send(type, payload);
};
const confirmIntents = (status: string, choices: string): void => {
  lastStatus = status;
  lastChoices = choices;
  for (const [type, it] of pendingIntents) {
    const done = type === 'REROLL_EQUIP' ? choices !== it.choices : status !== it.status;
    if (done) pendingIntents.delete(type);
  }
};
const flushPendingSends = (): void => {
  if (!gameRoom) return;
  for (const [type, it] of pendingIntents) gameRoom.send(type, it.payload);
};
const postNet = (state: 'ok' | 'reconnecting' | 'lost'): void => {
  combatFrame ??= document.getElementById('combat-frame') as HTMLIFrameElement | null;
  combatFrame?.contentWindow?.postMessage({ type: 'net', state }, '*');
};

// Liveness watchdog: PING every HEARTBEAT_MS; if no PONG for HEARTBEAT_DEAD_MS the socket is
// treated as dead (half-open link — no close frame ever arrives) and closed on purpose, which
// drops us into the normal reconnect path (badge + queued intents + token reconnect).
const HEARTBEAT_MS = 4_000;
const HEARTBEAT_DEAD_MS = 10_000;
let lastPong = 0;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
const startHeartbeat = (): void => {
  if (heartbeatTimer) return;
  heartbeatTimer = setInterval(() => {
    if (!gameRoom) return;
    if (lastPong && Date.now() - lastPong > HEARTBEAT_DEAD_MS) {
      lastPong = 0;
      void gameRoom.leave(false); // unconsented close -> onLeave(code != 1000) -> attemptReconnect
      return;
    }
    gameRoom.send('PING');
  }, HEARTBEAT_MS);
};

const bindGameRoom = (room: Room): void => {
    // Exactly ONE room may feed the combat iframe. If a previous one is still bound (e.g. a
    // reconnect resolved while a fresh PLAY also joined), drop it consented before binding —
    // two rooms interleaving snapshots makes HP/sprites flicker between two games.
    if (gameRoom && gameRoom !== room) {
      const old = gameRoom;
      gameRoom = null;
      void old.leave(true);
    }
    gameRoom = room;
    saveReconnectToken(room.reconnectionToken);
    lastPong = Date.now();
    room.onMessage('PONG', () => { lastPong = Date.now(); });
    startHeartbeat();
    postNet('ok');
    flushPendingSends();

    room.onStateChange((state: Record<string, unknown>) => {
      const playerHp = (state.playerHp as number | undefined) ?? 0;
      const playerMaxHp = (state.playerMaxHp as number | undefined) ?? 100;
      const coins = (state.coins as number | undefined) ?? 0;
      const floor = (state.floor as number | undefined) ?? 1;
      const status = (state.status as string | undefined) ?? 'waiting';
      const warriorName = (state.warriorName as string | undefined) ?? '';
      const lastEvent = (state.lastEvent as string | undefined) ?? '';
      const lastEventDetail = (state.lastEventDetail as string | undefined) ?? '';
      const playerResistance = (state.playerResistance as number | undefined) ?? 0;
      const equippedJson = (state.equipped as string | undefined) ?? '{}';
      const slotLevelsJson = (state.slotLevels as string | undefined) ?? '{}';
      const setBonusLabel = (state.setBonus as string | undefined) ?? '';
      const playerAtkDmg = (state.playerAttackDamage as number | undefined) ?? 0;

      const bossDropResult = (state.bossDropResult as string | undefined) ?? '';

      const mob = state.mob as Record<string, unknown> | undefined;
      const mobHp = (mob?.hp as number | undefined) ?? 0;
      const mobMaxHp = (mob?.maxHp as number | undefined) ?? 1;
      const mobAlive = (mob?.alive as boolean | undefined) ?? false;
      const mobAttackTimer = (mob?.attackTimer as number | undefined) ?? 0;
      const mobAttackSpeedMs = (mob?.attackSpeedMs as number | undefined) ?? 1;
      const mobId = (mob?.id as string | undefined) ?? '';
      const mobIsBoss = (mob?.isBoss as boolean | undefined) ?? false;
      const mobKind = (mob?.kind as string | undefined) ?? '';

      const playerAttackTimer = (state.playerAttackTimer as number | undefined) ?? 0;
      const playerAttackSpeedMs = (state.playerAttackSpeedMs as number | undefined) ?? 1;

      const qteActive = (state.qteActive as boolean | undefined) ?? false;
      const qteId = (state.qteId as string | undefined) ?? '';

      const equipChoices = (state.equipChoices as string | undefined) ?? '[]';
      const equipped = (state.equipped as string | undefined) ?? '{}';
      const rerollCostVal = (state.rerollCost as number | undefined) ?? 0;

      const mobIntroText = (state.mobIntroText as string | undefined) ?? '';

      confirmIntents(status, equipChoices);

      const isFloorComplete = status === 'floor_complete';
      const isFighting = status === 'fighting';
      const isEnded = status === 'ended';
      const isIntro = status === 'mob_intro';
      const runId = (state.runId as string | undefined) ?? '';

      // Drive the pixel-art combat iframe with a plain GameState snapshot (fields/units per GameRoom.ts).
      const rerollCount = (state.rerollCount as number | undefined) ?? 0;
      const narrSnap = {
        status, floor, coins, playerHp, playerMaxHp, lastEvent, lastEventDetail, rerollCount,
        rerollCost: rerollCostVal, equipChoices, mob: { id: mobId, alive: mobAlive, isBoss: mobIsBoss },
      };
      narrator.newRun(runId);
      if (isIntro) {
        const key = runId + '@' + String(floor);
        if (key !== introKey) {
          introKey = key;
          const text = narrator.introLine(narrSnap);
          if (floor === 1) {
            // the only pause in a run: the Keeper's opening line, after the screen fades in
            void screenReady.then(() => { sayToCombat({ type: 'intro', text, floor }); });
          } else {
            // later floors keep the pace: fight starts at once, his line (if any) runs over it
            sendGame('START_FIGHT');
            if (text) sayToCombat({ type: 'say', text, where: 'box' });
          }
        }
      } else {
        narrator.onState(narrSnap);
      }
      postCombat({
        warriorName, status, playerHp, playerMaxHp,
        playerAttackTimer, playerAttackSpeedMs, playerAttackDamage: playerAtkDmg, playerResistance,
        coins, floor, lastEvent, lastEventDetail,
        equipChoices, rerollCost: rerollCostVal, rerollCount,
        qteActive, qteId, equipped,
        // stats panel v2 (2026-08-26): speed/heal/set/combo from the server, best floor from this browser
        setBonus: setBonusLabel,
        // v4 build stats (2026-08-26): crit / leech / block / focus from the server
        critPct: (state.critPct as number | undefined) ?? 0.05, critDmg: (state.critDmg as number | undefined) ?? 1.5,
        lifestealPct: (state.lifestealPct as number | undefined) ?? 0, blockPct: (state.blockPct as number | undefined) ?? 0,
        qteDmgPct: (state.qteDmgPct as number | undefined) ?? 0,
        focus: (state.focus as number | undefined) ?? 0,
        qteCombo: (state.qteCombo as number | undefined) ?? 0, bestFloor: bestFloorLocal(),
        mob: {
          id: mobId, hp: mobHp, maxHp: mobMaxHp,
          attackTimer: mobAttackTimer, attackSpeedMs: mobAttackSpeedMs, alive: mobAlive, isBoss: mobIsBoss,
          kind: mobKind,
        },
      });

      setText('combat-status', isFighting ? 'COMBAT' : isFloorComplete ? 'FLOOR COMPLETE' : isIntro ? 'ENCOUNTER' : status.toUpperCase());

      const introArea = $('combat-intro-area');
      const combatArea = $('combat-fight-area');
      const rewardArea = $('combat-reward-area');
      if (introArea) introArea.style.display = isIntro ? 'block' : 'none';
      if (combatArea) combatArea.style.display = isFighting ? 'block' : 'none';
      if (rewardArea) rewardArea.style.display = isFloorComplete ? 'block' : 'none';

      const floorAmbience = (state.floorAmbience as string | undefined) ?? '';
      if (isIntro) {
        setText('intro-floor', String(floor));
        setText('intro-ambience', floorAmbience);
        setText('intro-phrase', '"' + mobIntroText + '"');
        const mobLabel = mobIsBoss ? 'BOSS' : mobId.split('_').slice(0, 2).join('_').toUpperCase();
        setText('intro-mob-name', '— ' + mobLabel);
        const introEl = $('combat-intro-area');
        const box = introEl?.querySelector('.border-box');
        if (box) (box as HTMLElement).dataset.label = mobIsBoss ? 'boss encounter' : 'encounter';
      }

      if (isFloorComplete) {
        setText('reward-floor', String(floor));
        setText('reward-loot-summary', lastEventDetail);
        setText('reward-coins', String(coins) + ' total');
        setText('reward-hp', String(playerHp) + ' / ' + String(playerMaxHp));
        setText('reward-resistance', String(Math.round(playerResistance * 100)) + '%');
        setText('reward-atk', String(playerAtkDmg));
        renderRewardEquipped(equippedJson, slotLevelsJson);
        const playerAtkSpdMs = (state.playerAttackSpeedMs as number | undefined) ?? 1500;
        const playerHealPct = 0.01;
        renderEquipment(equipChoices, rerollCostVal, coins, {
          atkDmg: playerAtkDmg,
          atkSpdMs: playerAtkSpdMs,
          resistance: playerResistance,
          maxHp: playerMaxHp,
          healOnKillPct: playerHealPct,
        });

        const dropEl = $('reward-boss-drop');
        if (dropEl) {
          if (bossDropResult) {
            dropEl.style.display = 'block';
            setText('boss-drop-text', bossDropResult);
          } else {
            dropEl.style.display = 'none';
          }
        }
      }

      setText('combat-warrior-name', warriorName.toUpperCase());
      setText('combat-hp-bar', buildBar(playerHp, playerMaxHp, 30));
      setText('combat-hp-text', String(playerHp) + ' / ' + String(playerMaxHp));
      setText('combat-coins', String(coins));
      setText('combat-floor', String(floor));
      setText('combat-resistance', String(Math.round(playerResistance * 100)) + '%');

      let equipCount = 0;
      try {
        equipCount = Object.keys(JSON.parse(equippedJson) as Record<string, unknown>).length;
      } catch { /* empty */ }
      setText('combat-equip-count', String(equipCount) + '/5');
      setText('combat-set-bonus', setBonusLabel);
      renderEquipPanel(equippedJson, slotLevelsJson);

      const mobDisplayName = mobIsBoss ? 'BOSS' : mobId.split('_').slice(0, 2).join('_').toUpperCase();
      setText('combat-mob-name', mobAlive ? mobDisplayName : 'DEFEATED');
      setText('combat-mob-hp-bar', mobAlive ? buildBar(mobHp, mobMaxHp, 20) : '░'.repeat(20));
      setText('combat-mob-hp-text', mobAlive ? String(mobHp) + ' / ' + String(mobMaxHp) : '0');

      const atkPct = Math.min(100, Math.round((mobAttackTimer / mobAttackSpeedMs) * 100));
      const atkBarWidth = 25;
      const atkFilled = Math.round((atkPct / 100) * atkBarWidth);
      setText('combat-atk-bar', (mobAlive ? '▓'.repeat(atkFilled) : '') + '░'.repeat(atkBarWidth - atkFilled));
      setText('combat-atk-pct', mobAlive ? String(atkPct) + '%' : '--');

      const playerAtkPct = Math.min(100, Math.round((playerAttackTimer / playerAttackSpeedMs) * 100));
      const pAtkFilled = Math.round((playerAtkPct / 100) * atkBarWidth);
      setText('combat-player-atk-bar', '▓'.repeat(pAtkFilled) + '░'.repeat(atkBarWidth - pAtkFilled));
      setText('combat-player-atk-pct', String(playerAtkPct) + '%');

      // QTE
      const qteEl = $('qte-zone');
      const qteWindowMs = (state.qteWindowMs as number | undefined) ?? 0;
      if (qteEl) qteEl.style.display = qteActive ? 'block' : 'none';
      if (qteActive) {
        const qteBtn = $('btn-qte');
        if (qteBtn) qteBtn.dataset.qteId = qteId;
        if (qteId !== lastQteAnimated && qteWindowMs > 0) {
          lastQteAnimated = qteId;
          const timerFill = $('qte-timer-fill');
          if (timerFill) {
            timerFill.style.transition = 'none';
            timerFill.style.width = '100%';
            void timerFill.offsetWidth;
            timerFill.style.transition = 'width ' + String(qteWindowMs) + 'ms linear';
            timerFill.style.width = '0%';
          }
        }
      }

      const eventKey = lastEvent + ':' + lastEventDetail;
      if (lastEvent && eventKey !== lastLoggedKey) {
        lastLoggedKey = eventKey;
        const eventText = lastEvent + ' ' + lastEventDetail;
        let cls = '';
        if (lastEvent === 'mob_attack') cls = 'dmg-taken';
        else if (lastEvent === 'player_attack') cls = 'dmg-dealt';
        else if (lastEvent === 'mob_killed') cls = 'kill';
        else if (lastEvent === 'boss_killed') cls = 'boss';
        else if (lastEvent === 'qte_hit') cls = 'qte-hit';
        else if (lastEvent === 'qte_miss') cls = 'dmg-taken';
        else if (lastEvent === 'equipped') cls = 'drop';
        addLog(eventText, cls);
      }

      if (isEnded) {
        setText('combat-status', 'RUN OVER');
        clearReconnectToken(); // run is over — don't try to resume it on a later reload
        setText('end-name', warriorName);
        addLog('--- RUN OVER --- score: ' + String(coins), 'boss');
        const endInfo = $('combat-end-info');
        if (endInfo) {
          endInfo.style.display = 'block';
          setText('end-score', String(coins));
          setText('end-floor', String(floor));
        }
      } else {
        const endInfo = $('combat-end-info');
        if (endInfo) endInfo.style.display = 'none';
      }
    });

    room.onLeave((code: number) => {
      // A room we already replaced (bindGameRoom leaves the old one) reports its close later —
      // that must not touch the CURRENT room's state, or we'd "reconnect" to a live room forever.
      if (gameRoom !== room) return;
      gameRoom = null;
      // Consented close: 1000 (normal) or 4000 (Colyseus WS_CLOSE_CONSENTED, what room.leave(true)
      // produces) — intentional leave or the run ended, nothing to resume.
      if (code === 1000 || code === 4000) {
        clearReconnectToken();
        return;
      }
      // Unexpected drop: the server is holding the room open (allowReconnection). Try to reconnect
      // in place; a full reload takes the same token path via restoreSession().
      setText('combat-status', 'RECONNECTING…');
      postNet('reconnecting');
      void attemptReconnect();
    });
};

// Reconnect (resume) an existing run, or join/create a fresh one. Returns false on failure so the
// caller decides what to do (keep the token and retry, or fall back to sign-in).
let connecting = false;
const connectGame = async (reconnectToken?: string): Promise<boolean> => {
  if (!auth || !client) return false;
  const wasConnecting = connecting;
  connecting = true;
  let room: Room;
  try {
    room = reconnectToken
      ? await client.reconnect(reconnectToken)
      : await client.joinOrCreate('game', { wallet: auth.wallet });
  } catch {
    return false;
  } finally {
    connecting = wasConnecting; // restoreSession may be holding the flag across a longer window
  }
  bindGameRoom(room);
  showScreen('combat');
  return true;
};

// In-place reconnection loop for a live-page network blip (a reload goes through restoreSession
// instead). Bounded by the server's reconnection window; gives up and clears the stale token after.
let reconnecting = false;
const attemptReconnect = async (): Promise<void> => {
  if (reconnecting || !client) return;
  reconnecting = true;
  const deadline = Date.now() + RECONNECT_WINDOW_MS;
  while (Date.now() < deadline) {
    const token = loadReconnectToken();
    if (!token) break;
    if (await connectGame(token)) {
      reconnecting = false;
      return;
    }
    await sleep(RECONNECT_RETRY_MS);
  }
  reconnecting = false;
  clearReconnectToken();
  setText('combat-status', 'DISCONNECTED — reload to resume');
  postNet('lost'); // stop the "reconnecting" blink; tell the player to reload
};

const startCombat = async (): Promise<void> => {
  if (!auth || !client) return;
  // Already in a run (or a reconnect is mid-flight after a reload)? Never open a second room —
  // just make sure the combat screen is showing.
  if (gameRoom || connecting) {
    showScreen('combat');
    return;
  }
  // PLAY: fade the menu to black, switch screens underneath, connect, fade the dungeon in.
  // The floor-1 intro line waits for the fade-in (screenReady) so it starts on a settled screen.
  let fadedIn: () => void = () => undefined;
  screenReady = new Promise<void>((resolve) => { fadedIn = resolve; });
  await fade(true);
  showScreen('combat');
  clearLog();
  setText('combat-status', 'connecting...');
  const ok = await connectGame();
  if (!ok) setText('combat-status', 'FAILED TO CONNECT');
  await fade(false, 450);
  fadedIn();
};

const handleDevSignIn = async (): Promise<void> => {
  const btn = $('btn-dev') as HTMLButtonElement | null;
  if (btn) btn.disabled = true;
  showError('');

  try {
    auth = await devSignIn();
    saveAuth(auth);
    await enterLobby();
  } catch (err) {
    const msg = err instanceof Error ? err.message : 'sign-in failed';
    showError(msg);
    if (btn) btn.disabled = false;
  }
};

// Restore a persisted session on load: valid auth skips the connect-wallet screen; a live
// reconnection token resumes the run in progress (Camada 1). Stale/expired → normal sign-in.
// Playtest mode (2026-09-03): no connect-wallet screen — a first visit signs in silently with a
// per-browser dev keypair and lands on the lobby (PLAY / LEADERBOARD). The sign-in screen only
// appears if that silent sign-in fails (backend unreachable). Flip off when real wallets arrive.
const PLAYTEST_AUTO_SIGNIN = true;

const restoreSession = async (): Promise<void> => {
  const saved = loadAuth();
  if (!saved) {
    if (PLAYTEST_AUTO_SIGNIN) {
      try {
        auth = await devSignIn();
        saveAuth(auth);
        await enterLobby();
      } catch {
        showScreen('sign-in');   // backend unreachable: fall back to the explicit button
      }
      return;
    }
    showScreen('sign-in');
    return;
  }
  auth = saved;
  const token = loadReconnectToken();
  // Hold PLAY for the whole restore (the lobby is visible while /health + reconnect are in
  // flight, and a PLAY there would open a SECOND room next to the one being resumed).
  if (token) connecting = true;
  try {
    await enterLobby();
    if (token && !(await connectGame(token))) clearReconnectToken();
  } finally {
    connecting = false;
  }
};

const init = (): void => {
  const devBtn = $('btn-dev');
  if (devBtn) devBtn.addEventListener('click', () => void handleDevSignIn());

  const playBtn = $('btn-play');
  if (playBtn) playBtn.addEventListener('click', () => void startCombat());

  const fightBtn = $('btn-fight');
  if (fightBtn) fightBtn.addEventListener('click', () => { sendGame('START_FIGHT'); });

  const qteBtn = $('btn-qte');
  if (qteBtn) {
    qteBtn.addEventListener('click', () => {
      const qteId = qteBtn.dataset.qteId;
      if (qteId) sendGame('QTE_CLICK', { qteId });
    });
  }

  void restoreSession();
};

init();
